import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import moment from 'moment';
import VueClamp from 'vue-clamp';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faFilePdf, faFileWord, faFileExcel, faFileImage, faFileAlt, faFileCode, faFileArchive, faFilePowerpoint, faFileAudio, faFileVideo, faStickyNote,
} from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import TagsMenuList from '@/components/Tags/MenuList.vue';
import TagsDialogList from '@/components/Tags/DialogList.vue';
import TagChip from '@/components/Tags/Chip.vue';
import App from './App.vue';
import router from './router';
import store from './store';
import vuetify from './plugins/vuetify';
import isEmail from './plugins/isEmail';
import clientLogoUrl from './plugins/clientLogoUrl';
import { getSlugUrl, getSlugUrlWithoutProtocol } from './plugins/getSlugUrlPlugins';
import './plugins/apex';

if (window.navigator && navigator.serviceWorker) {
  navigator.serviceWorker.getRegistrations().then(registrations =>
    registrations.forEach(registration => registration.unregister())
  );
}

library.add(faFilePdf, faFileWord, faFileExcel, faFileImage, faFileAlt, faFileCode, faFileArchive, faFilePowerpoint, faFileAudio, faFileVideo, faStickyNote);

Vue.component(`font-awesome-icon`, FontAwesomeIcon);

Vue.config.productionTip = false;

const { location } = window;
window.INDEEGO_BASE_URL = `${location.protocol}//${location.hostname}${location.port ? `:${location.port}` : ``}`;
window.INDEEGO_GLOBAL_BASE_URL = window.INDEEGO_BASE_URL.replace(/\/\/([^.]+)/g, `//app`);

// Vue.component(`confirm-dialog`, ConfirmDialog);

const requireComponent = require.context(
  `@/components/Generics`,
  false,
  /[A-Z]\w+\.(vue|js)$/
);

requireComponent.keys().forEach(fileName => {
  const componentConfig = requireComponent(fileName);

  const componentName = _.upperFirst(
    _.camelCase(
      fileName
        .split(`/`)
        .pop()
        .replace(/\.\w+$/, ``)
    )
  );

  Vue.component(
    componentName,
    componentConfig.default || componentConfig
  );
});

Vue.component(`v-clamp`, VueClamp);

store.dispatch(`model/auth/loadCredentials`);

const twoDigits = nb => {
  const res = `${nb}`;
  if (res.length === 1) {
    return `0${res}`;
  }

  return res;
};

const formatDateFilter = date => {
  let momentDate = moment(date, `YYYYMMDDHHmm`);
  if (!momentDate.isValid()) {
    momentDate = moment(date, `YYYYMMDD`);
  }

  if (momentDate.isValid()) {
    return momentDate.format(`DD/MM/YYYY`);
  }
  return ``;
};

const formatHourFilter = date => {
  const momentDate = moment(date, `YYYYMMDDHHmm`);
  if (momentDate.isValid()) {
    return momentDate.format(`HH[h]mm`);
  }
  return ``;
};

Vue.filter(`defaultInterventionDateLabel`, date => {
  if (!_.trim(date).length) {
    return `-`;
  }
  return date;
});

Vue.filter(`pluralize`, (word, nb, pluralSuffix = `s`, singularSuffix = ``) => {
  if (nb > 1) {
    return `${word}${pluralSuffix}`;
  }

  return `${word}${singularSuffix}`;
});

Vue.filter(`formatInterventionDate`, ({
  plannedStartAt, plannedEndAt, lower = false,
}) => {
  const plannedDaysDuration = moment(plannedEndAt, `YYYYMMDDHHmm`).diff(moment(plannedStartAt, `YYYYMMDDHHmm`), `days`) + 1;
  const fromDate = formatDateFilter(plannedStartAt);
  let formattedDate = `Le ${fromDate}`;
  if (plannedDaysDuration > 1) {
    const toDate = formatDateFilter(plannedEndAt);
    formattedDate = `Du ${fromDate} au ${toDate}`;
  } else if (plannedStartAt !== plannedEndAt) {
    const fromHour = formatHourFilter(plannedStartAt);
    const toHour = formatHourFilter(plannedEndAt);
    formattedDate = `${formattedDate} de ${fromHour} à ${toHour}`;
  }

  return lower ? _.lowerFirst(formattedDate) : formattedDate;
});

Vue.filter(`formatDate`, formatDateFilter);
Vue.filter(`formatHour`, formatHourFilter);

Vue.filter(`formatMinutesDuration`, duration => {
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;

  return `${hours}h${twoDigits(minutes)}`;
});

Vue.filter(`formatInterventionRecurrence`, recurrentIntervention => {
  const nb = recurrentIntervention.recurrenceInc;
  let label = `Tous les `;
  let recurrenceTypeLabel = ``;
  switch (recurrentIntervention.recurrenceType) {
    case `day`:
      label = `Tous les `;
      recurrenceTypeLabel = `jours`;
      break;
    case `week`:
      label = `Toutes les `;
      recurrenceTypeLabel = `semaines`;
      break;
    case `month`:
      label = `Tous les `;
      recurrenceTypeLabel = `mois`;
      break;
    case `year`:
      label = `Tous les `;
      recurrenceTypeLabel = `ans`;
      break;
    default:
      break;
  }

  if (nb > 1) {
    label = `${label} ${nb} ${recurrenceTypeLabel}`;
  } else {
    label = `${label} ${recurrenceTypeLabel}`;
  }

  return label;
});

Vue.filter(`clientInitials`, (companyName = null, maxLen = 8) => {
  const fullName = (companyName || ``).toUpperCase();
  let name = fullName;

  if (name.length <= maxLen) { return name; }

  const splittedName = name.split(` `);
  if (splittedName.length > 1) {
    name = `${splittedName.map(word => word.substring(0, 1)).join(`.`)}.`;
  }

  if (name.length <= maxLen) { return name; }

  return fullName.substring(0, maxLen);
});

// $isEmail plugin
Vue.use(isEmail);

// $clientLogoUrl plugin
Vue.use(clientLogoUrl);

// $getSlugUrl plugin
Vue.use(getSlugUrl);

// $getSlugUrlWithoutProtocol plugin
Vue.use(getSlugUrlWithoutProtocol);

Vue.component(`TagsMenuList`, TagsMenuList);
Vue.component(`TagsDialogList`, TagsDialogList);
Vue.component(`TagChip`, TagChip);

Vue.mixin({
  computed: {
    $canWrite() { return this.$store.getters[`platform/canWrite`]; },
    $isReadOnly() { return this.$store.getters[`platform/isReadOnly`]; },
    ...mapGetters(`model/client`, [
      `getPlanRight`,
    ]),
  },
});

new Vue({
  router,
  store,
  vuetify,
  render: (h) => h(App),
}).$mount(`#app`);
